import { Color3 } from '@babylonjs/core/Maths/math.color';
import { CreateGround } from '@babylonjs/core/Meshes/Builders/groundBuilder';

import App from '../App';
import { FloorPlan as FloorPlanInstance } from '../utils/FloorPlan';
import { KompanSettingsFromPlanner } from './SharedSettingTypes';
import { createGrid } from '../utils/GridAlternative';

import { AppStateManager } from '@egr/xbox/base-app/managers/Manager';

import { AudioExportData } from '@egr/room/modules/utils/BackgroundSound';

import { MainArticleElement } from '@egr/wcf/modules/cf';
import { PbrMaterial } from '@egr/wcf/modules/core/mdl';
import { SceneElement } from '@egr/wcf/modules/core/mdl';
import { contains } from '@egr/wcf/modules/utils/array';

interface ProductData {
    basketId: string;
    itemType: string;
    manufacturerId: string;
    seriesId: string;
    artNr_base?: string;
    artNr_final?: string;
}

export interface KompanSettingsManagerState extends KompanSettingsFromPlanner {
    floorPlanData: string | undefined;
    floorPlanMapData: string | undefined;
    productData: Map<string, { ProductData: ProductData}>;
    soundData?: {
        music?: AudioExportData;
        effect?: AudioExportData;
    };
}

export class KompanSettingsManager extends AppStateManager<KompanSettingsManagerState> {
    protected viewChangeTimeout: number | null = null;

    constructor() {
        super();
        this.setKompanSettings = this.setKompanSettings.bind(this);
        this.showGround = this.showGround.bind(this);
        this.setFloorPlanData = this.setFloorPlanData.bind(this);
        this.addProductData = this.addProductData.bind(this);
    }

    public setKompanSettings(data: KompanSettingsManagerState): void {
        this.updateState({
            ...data,
            // migration of old format of settings
            buildingsVisible: data.buildingsVisible ?? data.floorPlanMap?.buildingsVisible,
        });
    }

    public showGround(): void {
        if (this.state.floorPlanMap?.visible && this.state.floorPlanMapData != null) {
            const mapGround = new FloorPlanInstance(this.app.coreApp, true);
            mapGround.enabled = true;
            mapGround.setTexture(this.state.floorPlanMapData);

            const { width, height } = this.state.floorPlanMap;
            if (width == null || height == null) {
                console.warn('Missing size for floorplan map');
            } else {
                mapGround.setSize(width, height, false);
            }
        } else if (this.state.floorPlan?.visible && this.state.floorPlanData != null) {
            const planGround = new FloorPlanInstance(this.app.coreApp, false);
            planGround.enabled = true;
            planGround.setTexture(this.state.floorPlanData);

            const { width, height } = this.state.floorPlan;
            if (width == null || height == null) {
                console.warn('Missing size for floorplan');
            } else {
                planGround.setSize(width, height, false);
            }

            planGround.brightness = this.state.floorPlan.brightness ?? 0;
            planGround.visibility = this.state.floorPlan.opacity ?? 0;
            planGround.colorReduction = this.state.floorPlan.colorReduction ?? 0;
        } else if (this.state.groundType === 'solid' && this.state.groundColor != null) {
            const material = new PbrMaterial('', this.app.coreApp.model.materials);
            material.metallic = 0;
            material.roughness = 0.5;
            material.albedoColor = new Color3(
                this.state.groundColor.r / 255,
                this.state.groundColor.g / 255,
                this.state.groundColor.b / 255
            ).toLinearSpace();

            const sum: number = material.albedoColor.r + material.albedoColor.g + material.albedoColor.b;

            if (sum > 2.9) {
                if (!material.unlit) {
                    material.unlit = true;
                    material.emissiveColor = new Color3(1, 1, 1);
                }
            } else if (material.unlit) {
                material.unlit = false;
                material.emissiveColor = new Color3(0, 0, 0);
            }

            const floorPlane = CreateGround('floor', { width: 200, height: 200 }, this.app.coreApp.scene);
            floorPlane.receiveShadows = true;
            floorPlane.isPickable = false;
            floorPlane.material = material;
            floorPlane.position.y = -0.006;
            floorPlane.setEnabled(true);
        } else if (this.state.groundType === 'grid') {
            const grid = createGrid(this.app.coreApp.scene, 200);
            grid.setEnabled(true);
        }
    }

    public setFloorPlanData(data: string, isMap: boolean): void {
        if (isMap) {
            this.updateState({floorPlanMapData: data});
            return;
        }
        this.updateState({floorPlanData: data});
    }

    public addProductData(
        basketId: string,
        itemType: string,
        manufacturerId: string,
        seriesId: string,
        artNr_base?: string,
        artNr_final?: string
    ): void {
        if (this.state.productData == null) {
            return;
        }

        const tPD: ProductData = {} as ProductData;

        tPD.basketId = basketId;
        tPD.itemType = itemType;
        tPD.manufacturerId = manufacturerId;
        tPD.seriesId = seriesId;
        tPD.artNr_base = artNr_base;
        tPD.artNr_final = artNr_final;

        const tentry: { ProductData: ProductData} | undefined = this.state.productData.get(basketId);

        if (tentry == null) {
            this.state.productData.set(basketId, {ProductData: tPD});
        }
    }

    public canAddArticleARExport(pBasketId: string | null, pexcludeList: Array<string>): boolean {
        if (this.state.productData === undefined || pBasketId == null) {
            return (false);
        }

        const tentry: { ProductData: ProductData } | undefined = this.state.productData.get(pBasketId);

        if (tentry == null) {
            return (false);
        }

        const texclLowered: Array<string> = pexcludeList.map((name) => name.toLowerCase());

        if (!contains(texclLowered, tentry.ProductData.seriesId)) {
            return (true);
        }

        return (false);
    }

    public async getOFMLElements(pApp: App, pexcludeList: Array<string>): Promise<Array<SceneElement>> {
        const tRes: Array<SceneElement> = [];

        for (const tElement of pApp.coreApp.model.elements) {
            if (!(tElement instanceof MainArticleElement)) {
                continue;
            }

            if (this.canAddArticleARExport(tElement.basketId, pexcludeList)) {
                tRes.push(tElement);
            }
        }

        return (Promise.resolve(tRes));
    }

    protected getInitialState(): KompanSettingsManagerState {
        return {
            floorPlan: null,
            floorPlanData: undefined,
            floorPlanMap: null,
            floorPlanMapData: undefined,
            productData: new Map(),
            buildingsVisible: false,
            groundType: 'nofloor',
            groundColor: {r: 236, g: 236, b: 236},
        };
    }
}
